import { IAiModel, canonicalActionTagFor } from 'state/aiModels'

export type ActionCommandFlowIconName = 'audio' | 'image' | 'model' | 'text' | 'tool' | 'video'

export interface ActionCommandFlowOptions {
    tag?: string | null
    type?: 'model' | 'tool'
    modelType?: IAiModel['modelType'] | null
    canRemixImage?: boolean | null
    canRemixVideo?: boolean | null
    canRemixAudio?: boolean | null
}

export interface ActionCommandIconFlow {
    source: ActionCommandFlowIconName | null
    target: ActionCommandFlowIconName
}

export function inferActionCommandCanRemixImage(tag: string | null | undefined): boolean {
    const normalizedTag = tag?.toString().trim().toLowerCase() || ''
    if (/^(edit|animate)(_|$)/.test(normalizedTag)) return true
    return false
}

export function inferActionCommandModelType(
    tag: string | null | undefined,
): IAiModel['modelType'] | null {
    const normalizedTag = canonicalActionTagFor(tag)

    if (['minimaxmusic', 'suno', 'tts', 'clone'].includes(normalizedTag)) {
        return 'audio'
    }

    if (
        /^(animate)(_|$)/.test(normalizedTag) ||
        [
            'grokvideo',
            'grokvideo1.5',
            'minimax',
            'h3',
            'kling',
            'kling2',
            'kling3',
            'kling4',
            'luma',
            'veo2',
            'veo3',
            'sora2',
            'wan',
            'seedance',
            'seedance1',
            'seedance2',
            'stitch',
            'stitch_video',
        ].includes(normalizedTag)
    ) {
        return 'video'
    }

    if (
        /^(edit)(_|$)/.test(normalizedTag) ||
        [
            'flux',
            'flux2',
            'flux11',
            'dalle',
            'sd3',
            'sdl',
            'fluxfast',
            'flash',
            'nb',
            'nbpro',
            'gpt4o',
            'gptimage2',
            'gptimage2.5',
            'gptimage2.5sunburst',
            'grokimage',
            'grok_imagine',
            'imagine',
            'qwen',
        ].includes(normalizedTag)
    ) {
        return 'image'
    }

    return null
}

function iconForModelType(
    modelType: IAiModel['modelType'] | null | undefined,
): ActionCommandFlowIconName {
    switch (modelType) {
        case 'image':
            return 'image'
        case 'video':
            return 'video'
        case 'audio':
            return 'audio'
        case 'text':
            return 'text'
        default:
            return 'model'
    }
}

export function getActionCommandIconFlow(options: ActionCommandFlowOptions): ActionCommandIconFlow {
    if (options.type === 'tool') {
        return { source: null, target: 'tool' }
    }

    const target = iconForModelType(options.modelType ?? inferActionCommandModelType(options.tag))
    const canRemixImage = options.canRemixImage ?? inferActionCommandCanRemixImage(options.tag)

    if (canRemixImage) {
        return { source: 'image', target }
    }

    if (options.canRemixVideo) {
        return { source: 'video', target }
    }

    if (options.canRemixAudio) {
        return { source: 'audio', target }
    }

    return { source: 'text', target }
}
